import React from "react";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";

interface Props {
  eventData: any;
}

const ExperienceDetail = ({ eventData }: Props) => {
  const formattedStartDate = eventData?.start
    ? new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(eventData.start))
    : "";

  const formattedEndDate = eventData?.end
    ? new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(eventData.end))
    : "";

  const formattedStartTime = eventData?.start
    ? new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(new Date(eventData.start))
    : "";

  const formattedEndTime = eventData?.end
    ? new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(new Date(eventData.end))
    : "";

  return (
    <div className="w-full">
      {/* Event Images */}
      {eventData?.media && eventData.media.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-primaryText mb-4">Event Images</h3>
          <div className="flex items-center gap-4 flex-wrap">
            {eventData.media.slice(0, 4).map((media: any, index: number) => (
              <img
                key={index}
                className="w-[125px] h-[110px] rounded-lg object-cover border border-gray-200"
                src={concatPathWithBackendUrl(media.name)}
                alt={`Event image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Basic Information */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-primaryText mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              Name
            </span>
            <span className="text-sm text-tertiaryText">
              {eventData?.name || "-"}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              Event Type
            </span>
            <span className="text-sm text-tertiaryText">
              {eventData?.eventType || "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-primaryText mb-4">Description</h3>
        <div className="flex flex-col gap-2">
          <span className="text-sm text-tertiaryText leading-relaxed">
            {eventData?.description || "No description available"}
          </span>
        </div>
      </div>

      {/* Date and Time Information */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-primaryText mb-4">Date & Time</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              Start Date
            </span>
            <span className="text-sm text-tertiaryText">
              {formattedStartDate || "-"}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              Start Time
            </span>
            <span className="text-sm text-tertiaryText">
              {formattedStartTime || "-"}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              End Date
            </span>
            <span className="text-sm text-tertiaryText">
              {formattedEndDate || "-"}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              End Time
            </span>
            <span className="text-sm text-tertiaryText">
              {formattedEndTime || "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Meeting Link */}
      {eventData?.meeting_link && eventData.meeting_link !== "string" && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-primaryText mb-4">Meeting Information</h3>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-captionText">
              Meeting Link
            </span>
            <a
              href={eventData.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ambassadorBlue hover:underline break-all"
            >
              {eventData.meeting_link}
            </a>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-primaryText mb-4">Additional Information</h3>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-captionText">
            Created At
          </span>
          <span className="text-sm text-tertiaryText">
            {eventData?.createdAt
              ? new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                }).format(new Date(eventData.createdAt))
              : "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ExperienceDetail;
